import { useState, useEffect } from "react";
import { ref, onValue, off } from "firebase/database";
import { rtdb } from "@/config/firebase";

/* ─── Types ─── */
export interface RealtimeDepositProgress {
    current: number;
    target: number;
    percentage: number;
    updatedAt: number;
}

export interface RealtimeMembers {
    count: number;
    updatedAt: number;
}

export interface RealtimeSocialHype {
    score: number;
    tweetCount: number;
    updatedAt: number;
}

export interface RealtimeStatus {
    value: "recruiting" | "launched" | "failed";
    reason?: string;
    updatedAt: number;
}

export interface RealtimeLockStatus {
    status: "none" | "locked" | "unlocked" | "partially_unlocked";
    currentMcap: number;
    mcapProgress: number;
    targetMcap: number;
    updatedAt: number;
}

export interface RealtimeGraduation {
    isGraduated: boolean;
    updatedAt: number;
}

export interface RealtimeCabalData {
    depositProgress: RealtimeDepositProgress | null;
    members: RealtimeMembers | null;
    socialHype: RealtimeSocialHype | null;
    status: RealtimeStatus | null;
    lockStatus: RealtimeLockStatus | null;
    graduation: RealtimeGraduation | null;
}

/**
 * Hook to subscribe to real-time cabal data from Firebase RTDB.
 * Returns live deposit progress, member count, social hype, and status.
 * Automatically cleans up listeners on unmount.
 */
export function useRealtimeCabal(cabalSlug: string | undefined): RealtimeCabalData {
    const [depositProgress, setDepositProgress] = useState<RealtimeDepositProgress | null>(null);
    const [members, setMembers] = useState<RealtimeMembers | null>(null);
    const [socialHype, setSocialHype] = useState<RealtimeSocialHype | null>(null);
    const [status, setStatus] = useState<RealtimeStatus | null>(null);
    const [lockStatus, setLockStatus] = useState<RealtimeLockStatus | null>(null);
    const [graduation, setGraduation] = useState<RealtimeGraduation | null>(null);

    useEffect(() => {
        if (!cabalSlug) return;

        const depositRef = ref(rtdb, `cabals/${cabalSlug}/depositProgress`);
        const membersRef = ref(rtdb, `cabals/${cabalSlug}/members`);
        const hypeRef = ref(rtdb, `cabals/${cabalSlug}/socialHype`);
        const statusRef = ref(rtdb, `cabals/${cabalSlug}/status`);
        const lockStatusRef = ref(rtdb, `cabals/${cabalSlug}/lockStatus`);
        const graduationRef = ref(rtdb, `cabals/${cabalSlug}/graduation`);

        onValue(depositRef, (snap) => {
            if (snap.exists()) setDepositProgress(snap.val());
        });

        onValue(membersRef, (snap) => {
            if (snap.exists()) setMembers(snap.val());
        });

        onValue(hypeRef, (snap) => {
            if (snap.exists()) setSocialHype(snap.val());
        });

        onValue(statusRef, (snap) => {
            if (snap.exists()) setStatus(snap.val());
        });

        onValue(lockStatusRef, (snap) => {
            if (snap.exists()) setLockStatus(snap.val());
        });

        onValue(graduationRef, (snap) => {
            if (snap.exists()) setGraduation(snap.val());
        });

        return () => {
            off(depositRef);
            off(membersRef);
            off(hypeRef);
            off(statusRef);
            off(lockStatusRef);
            off(graduationRef);
        };
    }, [cabalSlug]);

    return { depositProgress, members, socialHype, status, lockStatus, graduation };
}
